import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tables } from "@/integrations/supabase/types";
import { TrendingUp, TrendingDown, DollarSign, Calculator, Download, Shield, Pencil, Trash2, X, Check, FileText } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { exportToExcel } from "@/utils/excelExport";
import { toast } from "sonner";

type Vehicle = Tables<"vehicles">;
type VehicleExpense = Tables<"vehicle_expenses">;

// Temporary type definition until types are regenerated
type VehicleRevenue = {
  id: string;
  category: string;
  description: string | null;
  amount: number;
  hst_amount: number;
  revenue_date: string;
};

interface VehicleFinancialSummaryProps {
  vehicle: Vehicle;
  expenses: VehicleExpense[];
  revenue?: VehicleRevenue[]; // Optional for backward compatibility
  onVehicleUpdated?: () => void;
}

export function VehicleFinancialSummary({ vehicle, expenses, revenue = [], onVehicleUpdated }: VehicleFinancialSummaryProps) {
  const [isEditingWarranty, setIsEditingWarranty] = useState(false);
  const [editWarrantyName, setEditWarrantyName] = useState("");
  const [editWarrantyAmount, setEditWarrantyAmount] = useState("");
  
  const purchaseCost = Number(vehicle.purchase_cost) || 0;
  const purchaseHst = Number(vehicle.purchase_hst) || 0;
  const tradeInValue = Number(vehicle.trade_in_value) || 0;
  
  // Expenses calculations
  const totalExpenses = expenses.reduce((sum, e) => sum + Number(e.amount), 0);
  const totalExpenseHst = expenses.reduce((sum, e) => sum + Number(e.hst_amount), 0);
  const totalInvested = purchaseCost + totalExpenses;
  const totalItcs = purchaseHst + totalExpenseHst;

  // Revenue calculations
  const hasRevenueItems = revenue.length > 0;
  const totalRevenueAmount = revenue.reduce((sum, r) => sum + Number(r.amount), 0);
  const totalRevenueHst = revenue.reduce((sum, r) => sum + Number(r.hst_amount), 0);

  // Use revenue items if they exist, otherwise fallback to legacy vehicle fields
  const soldPrice = hasRevenueItems ? totalRevenueAmount : (Number(vehicle.sold_price) || 0);
  const hstCollectedManual = hasRevenueItems ? totalRevenueHst : (Number(vehicle.hst_collected) || 0);
  
  // Legacy warranty (if not in revenue items)
  const legacyWarrantyAmount = Number(vehicle.warranty_amount) || 0;
  const legacyWarrantyName = vehicle.warranty_name || "";
  const hasWarrantyInRevenue = revenue.some(r => r.category === "Warranty");
  const displayWarrantyAmount = hasWarrantyInRevenue ? 0 : legacyWarrantyAmount;

  const isSold = vehicle.status === "sold" && soldPrice > 0;

  // When hst_collected is explicitly set (e.g. from deal worksheet), sold_price is pre-HST
  // When hst_collected is not set, sold_price may be HST-inclusive (legacy), so extract HST
  const hstExplicitlySet = !hasRevenueItems && hstCollectedManual > 0;
  const hstCollected = isSold 
    ? (hasRevenueItems ? totalRevenueHst : (hstExplicitlySet ? hstCollectedManual : parseFloat(((soldPrice - tradeInValue) * 13 / 113).toFixed(2)))) 
    : 0;

  // When HST is explicitly provided, sold_price is already pre-HST (revenue = sold_price)
  // When HST is auto-calculated, sold_price is assumed HST-inclusive (legacy fallback)
  const actualSaleRevenue = isSold
    ? (hasRevenueItems ? totalRevenueAmount : (hstExplicitlySet ? soldPrice : soldPrice - hstCollected))
    : 0;

  const netHstPayable = isSold ? parseFloat((hstCollected - totalItcs).toFixed(2)) : 0;
  const netProfit = isSold ? parseFloat((actualSaleRevenue - totalInvested).toFixed(2)) : 0;

  const exportDealSummary = async () => {
    const vehicleName = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;

    const summaryData = [
      { Item: "Vehicle", Value: vehicleName },
      { Item: "VIN", Value: vehicle.vin },
      { Item: "Purchase Date", Value: vehicle.purchase_date || "" },
      { Item: "Sold Date", Value: vehicle.sold_date || "" },
      { Item: "", Value: "" },
      { Item: "--- PURCHASE ---", Value: "" },
      { Item: "Purchase Cost (excl. HST)", Value: purchaseCost.toFixed(2) },
      { Item: "HST Paid on Purchase (ITC)", Value: purchaseHst.toFixed(2) },
      { Item: "", Value: "" },
      { Item: "--- EXPENSES ---", Value: "" },
    ];

    expenses.forEach((e) => {
      summaryData.push({
        Item: `${e.category}${e.description ? ` - ${e.description}` : ""} (${new Date(e.expense_date + "T00:00:00").toLocaleDateString("en-CA")})`,
        Value: Number(e.amount).toFixed(2),
      });
    });

    summaryData.push(
      { Item: "Total Expenses (excl. HST)", Value: totalExpenses.toFixed(2) },
      { Item: "HST on Expenses (ITC)", Value: totalExpenseHst.toFixed(2) },
      { Item: "", Value: "" },
      { Item: "--- TOTAL INVESTMENT ---", Value: "" },
      { Item: "Total Invested (Purchase + Expenses)", Value: totalInvested.toFixed(2) },
      { Item: "Total ITCs Claimable", Value: totalItcs.toFixed(2) },
    );

    if (isSold) {
      summaryData.push(
        { Item: "", Value: "" },
        { Item: "--- REVENUE / SALE ---", Value: "" },
      );

      if (hasRevenueItems) {
        revenue.forEach((r) => {
          summaryData.push({
            Item: `${r.category}${r.description ? ` - ${r.description}` : ""} (${new Date(r.revenue_date + "T00:00:00").toLocaleDateString("en-CA")})`,
            Value: Number(r.amount).toFixed(2),
          });
        });
        summaryData.push({ Item: "Total Revenue (excl. HST)", Value: totalRevenueAmount.toFixed(2) });
        summaryData.push({ Item: "Total HST Collected", Value: totalRevenueHst.toFixed(2) });
      } else {
        summaryData.push(
          { Item: "Sale Price", Value: soldPrice.toFixed(2) },
          { Item: "HST Collected", Value: hstCollected.toFixed(2) },
          { Item: "Trade-in Value", Value: tradeInValue.toFixed(2) },
        );
        if (legacyWarrantyAmount > 0) {
          summaryData.push({ Item: `Warranty: ${legacyWarrantyName}`, Value: legacyWarrantyAmount.toFixed(2) });
        }
      }

      summaryData.push(
        { Item: "", Value: "" },
        { Item: "--- P&L ---", Value: "" },
        { Item: "Net Revenue (excl. HST)", Value: actualSaleRevenue.toFixed(2) },
        { Item: "Cost of Vehicle (Purchase + Expenses)", Value: `-${totalInvested.toFixed(2)}` },
        { Item: "Net Profit", Value: netProfit.toFixed(2) },
        { Item: "", Value: "" },
        { Item: "--- HST REMITTANCE (This Vehicle) ---", Value: "" },
        { Item: "HST Collected", Value: hstCollected.toFixed(2) },
        { Item: "Less: ITCs (Purchase + Expenses)", Value: `-${totalItcs.toFixed(2)}` },
        { Item: "Net HST Payable to CRA", Value: netHstPayable.toFixed(2) },
      );
    }

    const sheets = [{ name: "Deal Summary", data: summaryData }];

    if (expenses.length > 0) {
      sheets.push({
        name: "Expense Details",
        data: expenses.map((e) => ({
          Date: new Date(e.expense_date + "T00:00:00").toLocaleDateString("en-CA"),
          Category: e.category,
          Description: e.description || "",
          "Amount (excl. HST)": Number(e.amount).toFixed(2),
          "HST Paid (ITC)": Number(e.hst_amount).toFixed(2),
          Total: (Number(e.amount) + Number(e.hst_amount)).toFixed(2),
        })),
      });
    }

    if (revenue.length > 0) {
      sheets.push({
        name: "Revenue Details",
        data: revenue.map((r) => ({
          Date: new Date(r.revenue_date + "T00:00:00").toLocaleDateString("en-CA"),
          Category: r.category,
          Description: r.description || "",
          "Amount (excl. HST)": Number(r.amount).toFixed(2),
          "HST Collected": Number(r.hst_amount).toFixed(2),
          Total: (Number(r.amount) + Number(r.hst_amount)).toFixed(2),
        })),
      });
    }

    await exportToExcel(sheets, `DealSummary_${vehicle.year}_${vehicle.make}_${vehicle.model}_${vehicle.vin}.xlsx`);
    toast.success("Deal summary exported");
  };

  return (
    <div className="space-y-4">
      {/* Cost Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-1">Purchase Cost</p>
            <p className="text-lg font-bold">${purchaseCost.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
            {purchaseHst > 0 && (
              <p className="text-xs text-muted-foreground">+${purchaseHst.toFixed(2)} HST</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-1">Total Expenses</p>
            <p className="text-lg font-bold">${totalExpenses.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
            {totalExpenseHst > 0 && (
              <p className="text-xs text-muted-foreground">+${totalExpenseHst.toFixed(2)} HST</p>
            )}
          </CardContent>
        </Card>
        <Card className="border-primary/30 bg-primary/5 col-span-2 sm:col-span-1">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-1">Total Invested</p>
            <p className="text-lg font-bold text-primary">${totalInvested.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
            <p className="text-xs text-muted-foreground">ITCs: ${totalItcs.toFixed(2)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Sale & Profit (only when sold) */}
      {isSold && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                  <DollarSign className="h-3 w-3" /> Sale Revenue
                </p>
                <p className="text-lg font-bold">${actualSaleRevenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                {!hasRevenueItems && tradeInValue > 0 && (
                  <p className="text-xs text-muted-foreground">Trade-in: ${tradeInValue.toFixed(2)}</p>
                )}
                {hasRevenueItems && <p className="text-xs text-muted-foreground">{revenue.length} items</p>}
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                  <Calculator className="h-3 w-3" /> HST Collected
                </p>
                <p className="text-lg font-bold">${hstCollected.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                <p className="text-xs text-muted-foreground">
                  {hasRevenueItems ? "from revenue list" : (hstCollectedManual > 0 ? "manually entered" : "auto-calculated")}
                </p>
              </CardContent>
            </Card>
            <Card className={netHstPayable >= 0 ? "border-amber-300/50 bg-amber-50/50" : "border-green-300/50 bg-green-50/50"}>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground mb-1">Net HST Payable</p>
                <p className={`text-lg font-bold ${netHstPayable >= 0 ? "text-amber-700" : "text-green-700"}`}>
                  {netHstPayable >= 0 ? "" : "−"}${Math.abs(netHstPayable).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-muted-foreground">
                  {netHstPayable < 0 ? "ITC credit" : "Owing to CRA"}
                </p>
              </CardContent>
            </Card>
            <Card className={netProfit >= 0 ? "border-green-300/50 bg-green-50/50" : "border-red-300/50 bg-red-50/50"}>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                  {netProfit >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  Net Profit
                </p>
                <p className={`text-lg font-bold ${netProfit >= 0 ? "text-green-700" : "text-red-700"}`}>
                  {netProfit >= 0 ? "" : "−"}${Math.abs(netProfit).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Legacy Warranty sold (only if no revenue items) */}
          {!hasRevenueItems && legacyWarrantyAmount > 0 && !isEditingWarranty && (
            <Card className="border-blue-300/50 bg-blue-50/50">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-blue-600" />
                  <div>
                    <p className="text-xs text-muted-foreground">Warranty Sold (Legacy)</p>
                    <p className="text-sm font-medium">{legacyWarrantyName}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-lg font-bold">${legacyWarrantyAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => {
                    setEditWarrantyName(legacyWarrantyName);
                    setEditWarrantyAmount(legacyWarrantyAmount.toString());
                    setIsEditingWarranty(true);
                  }}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Revenue Breakdown */}
          {hasRevenueItems && (
            <Card className="border-green-100 bg-green-50/20">
              <CardContent className="p-4">
                <p className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-green-600" /> Revenue Breakdown
                </p>
                <div className="space-y-1">
                  {revenue.map((r) => (
                    <div key={r.id} className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{r.category}</span>
                      <span className="font-medium">${Number(r.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                  ))}
                  <div className="border-t mt-2 pt-1 flex justify-between font-bold">
                    <span>Total Net Revenue</span>
                    <span>${totalRevenueAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Export Deal Summary */}
          <Button variant="outline" onClick={exportDealSummary} className="w-full sm:w-auto">
            <Download className="h-4 w-4 mr-2" /> Export Deal Summary
          </Button>
        </>
      )}

      {/* Listing Price vs Invested (when not sold) */}
      {!isSold && (
        <Card className={vehicle.price > totalInvested ? "border-green-300/50 bg-green-50/50" : "border-amber-300/50 bg-amber-50/50"}>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Listed Price vs Total Invested</p>
            <p className="text-sm mt-1">
              <span className="font-bold">${vehicle.price.toLocaleString()}</span>
              <span className="text-muted-foreground"> − </span>
              <span className="font-bold">${totalInvested.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              <span className="text-muted-foreground"> = </span>
              <span className={`font-bold ${vehicle.price - totalInvested >= 0 ? "text-green-700" : "text-red-700"}`}>
                ${(vehicle.price - totalInvested).toLocaleString(undefined, { minimumFractionDigits: 2 })} potential margin
              </span>
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
